import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Textarea, Select } from '@/components/ui'
import { Terminal, Copy, Check, Trash2, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

type Lang = 'python' | 'javascript' | 'php' | 'node_axios' | 'go' | 'java' | 'csharp' | 'ruby'

interface ParsedCurl {
  url: string
  method: string
  headers: Record<string, string>
  data: string | null
  cookies: string | null
  compressed: boolean
  insecure: boolean
}

function parseCurl(input: string): ParsedCurl | null {
  const raw = input.trim()
  if (!raw.startsWith('curl')) return null

  let s = raw
    .replace(/\\\n\s*/g, ' ')  // join line continuations
    .replace(/\\\r\n\s*/g, ' ')
    .trim()

  const result: ParsedCurl = {
    url: '',
    method: 'GET',
    headers: {},
    data: null,
    cookies: null,
    compressed: false,
    insecure: false,
  }

  // Remove 'curl ' prefix
  s = s.slice(4).trim()

  const tokens: string[] = []
  let current = ''
  let inSingle = false
  let inDouble = false
  let escape = false

  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (escape) { current += c; escape = false; continue }
    if (c === '\\') { escape = true; continue }
    if (c === "'" && !inDouble) { inSingle = !inSingle; continue }
    if (c === '"' && !inSingle) { inDouble = !inDouble; continue }
    if (c === ' ' && !inSingle && !inDouble) {
      if (current) { tokens.push(current); current = '' }
      continue
    }
    current += c
  }
  if (current) tokens.push(current)

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]

    if (t === '-X' || t === '--request') {
      result.method = tokens[++i]?.toUpperCase() || 'GET'
    } else if (t === '-H' || t === '--header') {
      const h = tokens[++i] || ''
      const colonIdx = h.indexOf(':')
      if (colonIdx > 0) {
        const key = h.slice(0, colonIdx).trim()
        const val = h.slice(colonIdx + 1).trim()
        result.headers[key] = val
      }
    } else if (t === '-d' || t === '--data' || t === '--data-raw' || t === '--data-binary' || t === '--data-urlencode') {
      result.data = tokens[++i] || ''
      if (result.method === 'GET') result.method = 'POST'
    } else if (t === '-b' || t === '--cookie') {
      result.cookies = tokens[++i] || ''
    } else if (t === '--compressed') {
      result.compressed = true
    } else if (t === '-k' || t === '--insecure') {
      result.insecure = true
    } else if (t.startsWith('http://') || t.startsWith('https://')) {
      result.url = t
    } else if (!t.startsWith('-') && !result.url) {
      result.url = t
    }
  }

  if (!result.url) return null
  return result
}

function escStr(s: string, quote: string): string {
  return s.replace(/\\/g, '\\\\').replace(new RegExp(quote, 'g'), '\\' + quote)
}

function toPython(c: ParsedCurl): string {
  const lines: string[] = ['import requests', '']
  const hdrs = Object.entries(c.headers)

  if (hdrs.length > 0) {
    lines.push('headers = {')
    hdrs.forEach(([k, v]) => lines.push(`    "${escStr(k, '"')}": "${escStr(v, '"')}",`))
    lines.push('}')
    lines.push('')
  }

  if (c.data) {
    lines.push(`data = '${escStr(c.data, "'")}'`)
    lines.push('')
  }

  const args: string[] = [`"${escStr(c.url, '"')}"`]
  if (hdrs.length > 0) args.push('headers=headers')
  if (c.data) args.push('data=data')
  if (c.insecure) args.push('verify=False')

  lines.push(`response = requests.${c.method.toLowerCase()}(${args.join(', ')})`)
  lines.push('print(response.text)')
  return lines.join('\n')
}

function toJavaScript(c: ParsedCurl): string {
  const opts: string[] = []
  opts.push(`  method: "${c.method}"`)

  const hdrs = Object.entries(c.headers)
  if (hdrs.length > 0) {
    opts.push(`  headers: {`)
    hdrs.forEach(([k, v]) => opts.push(`    "${escStr(k, '"')}": "${escStr(v, '"')}",`))
    opts.push(`  }`)
  }

  if (c.data) {
    opts.push(`  body: ${JSON.stringify(c.data)}`)
  }

  const lines = [
    `fetch("${escStr(c.url, '"')}", {`,
    opts.join(',\n'),
    `})`,
    `  .then(res => res.text())`,
    `  .then(data => console.log(data))`,
    `  .catch(err => console.error(err));`,
  ]
  return lines.join('\n')
}

function toNodeAxios(c: ParsedCurl): string {
  const lines: string[] = [`const axios = require("axios");`, '']
  const config: string[] = []
  config.push(`  method: "${c.method.toLowerCase()}"`)
  config.push(`  url: "${escStr(c.url, '"')}"`)

  const hdrs = Object.entries(c.headers)
  if (hdrs.length > 0) {
    config.push(`  headers: {`)
    hdrs.forEach(([k, v]) => config.push(`    "${escStr(k, '"')}": "${escStr(v, '"')}",`))
    config.push(`  }`)
  }

  if (c.data) {
    config.push(`  data: ${JSON.stringify(c.data)}`)
  }

  lines.push(`axios({`)
  lines.push(config.join(',\n'))
  lines.push(`})`)
  lines.push(`  .then(res => console.log(res.data))`)
  lines.push(`  .catch(err => console.error(err));`)
  return lines.join('\n')
}

function toPhp(c: ParsedCurl): string {
  const lines: string[] = ['<?php', '', '$ch = curl_init();', '']
  lines.push(`curl_setopt($ch, CURLOPT_URL, "${escStr(c.url, '"')}");`)
  lines.push('curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);')

  if (c.method !== 'GET') {
    lines.push(`curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${c.method}");`)
  }

  const hdrs = Object.entries(c.headers)
  if (hdrs.length > 0) {
    lines.push('curl_setopt($ch, CURLOPT_HTTPHEADER, [')
    hdrs.forEach(([k, v]) => lines.push(`    "${escStr(k, '"')}: ${escStr(v, '"')}",`))
    lines.push(']);')
  }

  if (c.data) {
    lines.push(`curl_setopt($ch, CURLOPT_POSTFIELDS, '${escStr(c.data, "'")}');`)
  }

  if (c.insecure) {
    lines.push('curl_setopt($ch, CURLOPT_SSL_VERIFYPEER, false);')
  }

  lines.push('')
  lines.push('$response = curl_exec($ch);')
  lines.push('curl_close($ch);')
  lines.push('echo $response;')
  return lines.join('\n')
}

function toGo(c: ParsedCurl): string {
  const lines: string[] = [
    'package main',
    '',
    'import (',
    '\t"fmt"',
    '\t"io"',
    '\t"net/http"',
  ]
  if (c.data) lines.push('\t"strings"')
  lines.push(')', '')
  lines.push('func main() {')

  if (c.data) {
    lines.push(`\tbody := strings.NewReader(${JSON.stringify(c.data)})`)
    lines.push(`\treq, err := http.NewRequest("${c.method}", "${escStr(c.url, '"')}", body)`)
  } else {
    lines.push(`\treq, err := http.NewRequest("${c.method}", "${escStr(c.url, '"')}", nil)`)
  }
  lines.push('\tif err != nil { panic(err) }')

  Object.entries(c.headers).forEach(([k, v]) => {
    lines.push(`\treq.Header.Set("${escStr(k, '"')}", "${escStr(v, '"')}")`)
  })

  lines.push('')
  lines.push('\tresp, err := http.DefaultClient.Do(req)')
  lines.push('\tif err != nil { panic(err) }')
  lines.push('\tdefer resp.Body.Close()')
  lines.push('\tdata, _ := io.ReadAll(resp.Body)')
  lines.push('\tfmt.Println(string(data))')
  lines.push('}')
  return lines.join('\n')
}

function toJava(c: ParsedCurl): string {
  const lines: string[] = [
    'import java.net.http.*;',
    'import java.net.URI;',
    '',
    'public class Main {',
    '    public static void main(String[] args) throws Exception {',
    '        HttpClient client = HttpClient.newHttpClient();',
  ]

  const bodyPart = c.data
    ? `HttpRequest.BodyPublishers.ofString(${JSON.stringify(c.data)})`
    : 'HttpRequest.BodyPublishers.noBody()'

  lines.push(`        HttpRequest.Builder builder = HttpRequest.newBuilder()`)
  lines.push(`            .uri(URI.create("${escStr(c.url, '"')}"))`)
  lines.push(`            .method("${c.method}", ${bodyPart})`)

  Object.entries(c.headers).forEach(([k, v]) => {
    lines.push(`            .header("${escStr(k, '"')}", "${escStr(v, '"')}")`)
  })

  lines.push('            ;')
  lines.push('        HttpResponse<String> response = client.send(builder.build(), HttpResponse.BodyHandlers.ofString());')
  lines.push('        System.out.println(response.body());')
  lines.push('    }')
  lines.push('}')
  return lines.join('\n')
}

function toCsharp(c: ParsedCurl): string {
  const lines: string[] = [
    'using System.Net.Http;',
    '',
    'var client = new HttpClient();',
  ]

  if (c.data) {
    lines.push(`var content = new StringContent(${JSON.stringify(c.data)});`)
    Object.entries(c.headers).forEach(([k, v]) => {
      if (k.toLowerCase() === 'content-type') {
        lines.push(`content.Headers.ContentType = new System.Net.Http.Headers.MediaTypeHeaderValue("${escStr(v, '"')}");`)
      }
    })
  }

  Object.entries(c.headers).forEach(([k, v]) => {
    if (k.toLowerCase() !== 'content-type') {
      lines.push(`client.DefaultRequestHeaders.Add("${escStr(k, '"')}", "${escStr(v, '"')}");`)
    }
  })

  const methodMap: Record<string, string> = { GET: 'GetAsync', POST: 'PostAsync', PUT: 'PutAsync', DELETE: 'DeleteAsync' }
  const method = methodMap[c.method] || 'SendAsync'

  if (c.data && (c.method === 'POST' || c.method === 'PUT')) {
    lines.push(`var response = await client.${method}("${escStr(c.url, '"')}", content);`)
  } else {
    lines.push(`var response = await client.${method}("${escStr(c.url, '"')}");`)
  }

  lines.push('var body = await response.Content.ReadAsStringAsync();')
  lines.push('Console.WriteLine(body);')
  return lines.join('\n')
}

function toRuby(c: ParsedCurl): string {
  const lines: string[] = [
    'require "net/http"',
    'require "uri"',
    '',
    `uri = URI.parse("${escStr(c.url, '"')}")`,
    `request = Net::HTTP::${c.method[0] + c.method.slice(1).toLowerCase()}.new(uri)`,
  ]

  Object.entries(c.headers).forEach(([k, v]) => {
    lines.push(`request["${escStr(k, '"')}"] = "${escStr(v, '"')}"`)
  })

  if (c.data) {
    lines.push(`request.body = '${escStr(c.data, "'")}'`)
  }

  lines.push('')
  lines.push('response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: uri.scheme == "https") do |http|')
  lines.push('  http.request(request)')
  lines.push('end')
  lines.push('puts response.body')
  return lines.join('\n')
}

const converters: Record<Lang, { label: string; fn: (c: ParsedCurl) => string }> = {
  python: { label: 'Python (requests)', fn: toPython },
  javascript: { label: 'JavaScript (fetch)', fn: toJavaScript },
  node_axios: { label: 'Node.js (axios)', fn: toNodeAxios },
  php: { label: 'PHP (cURL)', fn: toPhp },
  go: { label: 'Go (net/http)', fn: toGo },
  java: { label: 'Java (HttpClient)', fn: toJava },
  csharp: { label: 'C# (HttpClient)', fn: toCsharp },
  ruby: { label: 'Ruby (net/http)', fn: toRuby },
}

export function CurlConverter() {
  const [input, setInput] = useState('')
  const [lang, setLang] = useState<Lang>('python')
  const [output, setOutput] = useState('')
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')

  const convert = () => {
    setError('')
    setOutput('')

    if (!input.trim()) {
      toast.error('Please paste a cURL command')
      return
    }

    const parsed = parseCurl(input)
    if (!parsed) {
      setError('Could not parse cURL command. Make sure it starts with "curl".')
      return
    }

    try {
      const result = converters[lang].fn(parsed)
      setOutput(result)
      toast.success(`Converted to ${converters[lang].label}`)
    } catch {
      setError('Failed to convert. Check your cURL syntax.')
    }
  }

  const copyOutput = async () => {
    if (!output) return
    await navigator.clipboard.writeText(output)
    setCopied(true)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  const clear = () => {
    setInput('')
    setOutput('')
    setError('')
  }

  const sampleCurl = `curl 'https://api.example.com/users' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer token123' \\
  -d '{"name":"John","email":"john@test.com"}'`

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
            <Terminal className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">cURL Converter</h1>
            <p className="text-muted-foreground">Convert cURL commands to any programming language</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>cURL Command</CardTitle>
                <CardDescription>Paste your cURL command here</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setInput(sampleCurl)}>
                  Sample
                </Button>
                <Button variant="outline" size="sm" onClick={clear} disabled={!input}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`curl 'https://api.example.com/data' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"key":"value"}'`}
              className="font-mono text-sm min-h-[250px] resize-none"
            />

            <div className="flex items-center gap-3">
              <Select value={lang} onChange={(e) => setLang(e.target.value as Lang)} className="flex-1">
                {Object.entries(converters).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Select>
              <Button onClick={convert}>
                <ArrowRight className="h-4 w-4 mr-2" />
                Convert
              </Button>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Output</CardTitle>
                <CardDescription>{converters[lang].label}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={copyOutput} disabled={!output}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder="Converted code will appear here..."
              className="font-mono text-sm min-h-[300px] resize-none"
            />
          </CardContent>
        </Card>
      </div>

      {/* Supported Languages */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>Supported Languages</CardTitle>
          <CardDescription>Click to switch the output language</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(converters).map(([key, { label }]) => (
              <Button
                key={key}
                variant={lang === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setLang(key as Lang); if (input) convert() }}
                className="font-mono text-xs"
              >
                {label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
